import React, { useState } from "react";
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import type { VisualizationBase } from "@models/VisualizationBase";
import { getVisualizationBaseSource } from "@models/VisualizationBase";
import CustomBaseContainer from "./CustomBaseContainer";
import PreconfiguredBaseContainer from "./PreconfiguredBaseContainer";
import DisableCustomBaseConfirmDialog from "./DisableCustomBaseConfirmDialog";

type BaseMode = "all" | "custom" | "preconfigured";

interface BaseModeSectionProps {
  datasetId: string | null;
  isDisabled?: boolean;
  base: VisualizationBase;
  onChange: (base: VisualizationBase) => void;
}

const SWITCH_TITLE: Record<Exclude<BaseMode, "custom">, string> = {
  all: "Switch to All eligible respondents?",
  preconfigured: "Switch to Pre-configured base?",
};

function deriveMode(base: VisualizationBase): BaseMode {
  if (!base.enabled) return "all";
  return getVisualizationBaseSource(base) === "preconfigured"
    ? "preconfigured"
    : "custom";
}

// The three mutually-exclusive base choices (All / Custom / Pre-configured).
// Owns the whole base object and emits each change through onChange.
const BaseModeSection: React.FC<BaseModeSectionProps> = ({
  datasetId,
  isDisabled,
  base,
  onChange,
}) => {
  const [pendingTarget, setPendingTarget] = useState<BaseMode | null>(null);

  const mode = deriveMode(base);
  const hasCustomBaseData = !!base.name.trim() || base.conditions.length > 0;

  const applyMode = (target: BaseMode) => {
    if (target === "all") {
      onChange({ ...base, enabled: false });
    } else if (target === "custom") {
      onChange({ ...base, enabled: true, source: "custom" });
    } else {
      onChange({ ...base, enabled: true, source: "preconfigured" });
    }
  };

  const handleModeChange = (target: BaseMode) => {
    if (target === mode) return;
    // Leaving a populated Custom base needs the switch confirmation.
    if (mode === "custom" && target !== "custom" && hasCustomBaseData) {
      setPendingTarget(target);
      return;
    }
    applyMode(target);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Base
      </Typography>

      <RadioGroup
        value={mode}
        onChange={(e) => handleModeChange(e.target.value as BaseMode)}
        sx={{ mt: 1 }}
      >
        <FormControlLabel
          value="all"
          control={<Radio size="small" />}
          label="All eligible respondents"
          disabled={isDisabled}
        />
        <FormControlLabel
          value="custom"
          control={<Radio size="small" />}
          label="Custom base"
          disabled={isDisabled || !datasetId}
        />
        <FormControlLabel
          value="preconfigured"
          control={<Radio size="small" />}
          label="Pre-configured base"
          disabled={isDisabled || !datasetId}
        />
      </RadioGroup>

      {mode === "custom" && (
        <CustomBaseContainer
          datasetId={datasetId}
          isDisabled={isDisabled}
          base={base}
          onChange={onChange}
        />
      )}

      {mode === "preconfigured" && (
        <PreconfiguredBaseContainer
          datasetId={datasetId}
          isDisabled={isDisabled}
          base={base}
          onChange={onChange}
        />
      )}

      <DisableCustomBaseConfirmDialog
        open={pendingTarget !== null}
        title={pendingTarget && pendingTarget !== "custom" ? SWITCH_TITLE[pendingTarget] : undefined}
        onCancel={() => setPendingTarget(null)}
        onConfirm={() => {
          if (pendingTarget) applyMode(pendingTarget);
          setPendingTarget(null);
        }}
      />
    </Box>
  );
};

export default BaseModeSection;
